import { useCallback, useEffect, useRef } from 'react'

/**
 * Keep the audience window and the presenter view on the same slide
 * (MYD-497), over `BroadcastChannel('presentation:<id>')`.
 *
 * Either window can drive. Each posts the slide it moved to, and each follows
 * the other. A move that ARRIVED from the other window is not echoed back,
 * which is what stops the two bouncing a slide number between them forever.
 *
 * A window that opens later says `hello`, and whoever is already presenting
 * answers with where it is, so a presenter view opened mid-show starts on the
 * right slide rather than on slide 1.
 *
 * Each move carries the slide's id as well as its number (MYD-504). The two
 * windows resolve stories independently, and a story's continuation slides
 * appear only once it has, so for a moment the same number can name different
 * slides in each. The id is followed when the receiver has it.
 *
 * `BroadcastChannel` is same-origin and same-browser, which is exactly the
 * case: two windows of one browser on a laptop driving a projector. Where it
 * does not exist the hook does nothing, and each window is simply on its own.
 */

type Outgoing = { type: 'goto'; index: number; key?: string | null } | { type: 'hello' } | { type: 'ended' }
type Message = Outgoing & { sender: string }

interface UsePresentationSyncOptions {
	presentationId: string
	/** This window's current slide. */
	index: number
	/** Its id, so the other window can find it even where the numbering differs. */
	slideKey?: string | null
	/** Only sync once the deck is loaded and the window is presenting. */
	enabled: boolean
	/** The other window moved. */
	onRemoteGoto: (index: number, key: string | null) => void
	/** The audience window ended the presentation. */
	onEnded?: () => void
}

const channelName = (id: string) => `presentation:${id}`

export function usePresentationSync({ presentationId, index, slideKey = null, enabled, onRemoteGoto, onEnded }: UsePresentationSyncOptions) {
	const channel = useRef<BroadcastChannel | null>(null)
	const sender = useRef(`window-${Math.random().toString(36).slice(2, 10)}`)
	// The move most recently RECEIVED, so this window does not echo it back.
	const received = useRef<{ index: number; key: string | null } | null>(null)
	const indexRef = useRef(index)
	indexRef.current = index
	const keyRef = useRef(slideKey)
	keyRef.current = slideKey
	const handlers = useRef({ onRemoteGoto, onEnded })
	handlers.current = { onRemoteGoto, onEnded }

	const post = useCallback((message: Outgoing) => {
		channel.current?.postMessage({ ...message, sender: sender.current })
	}, [])

	useEffect(() => {
		if (!enabled || typeof BroadcastChannel === 'undefined') return
		const opened = new BroadcastChannel(channelName(presentationId))
		channel.current = opened

		opened.onmessage = (event: MessageEvent<Message>) => {
			const message = event.data
			if (!message || message.sender === sender.current) return
			if (message.type === 'goto' && Number.isInteger(message.index)) {
				const key = typeof message.key === 'string' ? message.key : null
				// Already there: by id when both know it, else by number.
				if (key && keyRef.current ? key === keyRef.current : message.index === indexRef.current) return
				received.current = { index: message.index, key }
				handlers.current.onRemoteGoto(message.index, key)
			} else if (message.type === 'hello') {
				opened.postMessage({ type: 'goto', index: indexRef.current, key: keyRef.current, sender: sender.current })
			} else if (message.type === 'ended') {
				handlers.current.onEnded?.()
			}
		}
		opened.postMessage({ type: 'hello', sender: sender.current })

		return () => {
			opened.close()
			channel.current = null
		}
	}, [enabled, presentationId])

	// Announce every LOCAL move. A move that came from the other window has
	// already been seen there, so it is swallowed here once.
	useEffect(() => {
		if (!enabled) return
		// Arrived where the other window asked: by id when it sent one, else by
		// number (the id is followed even where this window numbers it differently).
		const arrived = received.current
		if (arrived && (arrived.key && slideKey ? arrived.key === slideKey : arrived.index === index)) {
			received.current = null
			return
		}
		post({ type: 'goto', index, key: slideKey })
		// The id too: the same number can come to name a different slide.
	}, [index, slideKey, enabled, post])

	const announceEnded = useCallback(() => post({ type: 'ended' }), [post])

	return { announceEnded }
}

export default usePresentationSync
